// src/components/Help/MyTickets.jsx

import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, List, ListItem, ListItemText, Divider, Chip, CircularProgress, Skeleton } from '@mui/material';
import { getUserTickets } from '../../ctapi/supportService';
import { useAuth } from '../../hooks/useAuth';

const statusColors = {
  new: 'info',
  open: 'primary',
  pending: 'warning',
  solved: 'success',
  closed: 'default',
};

const TicketListItemSkeleton = () => (
    <ListItem disablePadding>
        <ListItemText
            primary={<Skeleton variant="text" width="60%" />}
            secondary={<Skeleton variant="text" width="40%" />}
        />
        <Skeleton variant="circular" width={60} height={24} />
    </ListItem>
);

export default function MyTickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.uuid) {
      const fetchTickets = async () => {
        setIsLoading(true);
        try {
          const response = await getUserTickets(user.uuid);
          setTickets(response.tickets || []);
        } catch (error) {
          console.error("Error fetching user tickets:", error);
          setTickets([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchTickets();
    } else {
      setIsLoading(false);
    }
  }, [user]);

  return (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" component="h2" sx={{ fontWeight: 500, mb: 2 }}>
          My Support Tickets
        </Typography>
        {isLoading ? (
          <List disablePadding>
            <TicketListItemSkeleton />
            <Divider component="li" sx={{ my: 1 }} />
            <TicketListItemSkeleton />
          </List>
        ) : tickets.length > 0 ? (
          <List disablePadding>
            {tickets.map((ticket, index) => (
              <React.Fragment key={ticket.ticket_id}>
                <ListItem disablePadding>
                  <ListItemText
                    primary={ticket.subject || `Ticket #${ticket.ticket_id}`}
                    secondary={`Created at: ${new Date(ticket.updated_at || ticket.created_at).toLocaleDateString()}`}
                  />
                  <Chip label={ticket.status || 'N/A'} color={statusColors[ticket.status] || 'default'} size="small" />
                </ListItem>
                {index < tickets.length - 1 && <Divider component="li" sx={{ my: 1 }} />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Typography color="text.secondary" variant="body2">You have no recent support tickets.</Typography>
        )}
      </CardContent>
    </Card>
  );
}
